import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line no-unused-vars
import { Table, Button, Modal, message, Alert, Divider, Space, Spin, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { getLabelsWithCountsThunk, deleteAndReassignLabelThunk } from '../Feature/LabelsSlice';
import { getCurrentSpaceThunk } from '../Feature/SpacesSlice';
import './LabelManagement.css';

export const LabelManagement = () => {
    const dispatch = useDispatch();
    const { spaceId } = useParams();

    const currentSpace = useSelector((state) => state.spaces.current);
    const currentUser = useSelector((state) => state.firebase.auth?.currentUser);
    const labelsWithCounts = useSelector((state) => state.labels.labelsWithCounts);
    const labelsStatus = useSelector((state) => state.labels.status);

    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [selectedReassignLabel, setSelectedReassignLabel] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Check if current user is the space owner
    const isOwner = currentSpace?.data?.spaceOwner?.uid === currentUser?.uid;

    useEffect(() => {
        if (!currentSpace.data || currentSpace.data.id !== spaceId) {
            dispatch(getCurrentSpaceThunk(spaceId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, spaceId]);

    useEffect(() => {
        // Only fetch labels once when spaceId changes
        if (spaceId) {
            dispatch(getLabelsWithCountsThunk(spaceId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, spaceId]);

    const handleDeleteClick = (label) => {
        setSelectedLabel(label);
        setSelectedReassignLabel(null);
        setDeleteModalVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedLabel) return;

        setIsDeleting(true);
        try {
            await dispatch(deleteAndReassignLabelThunk({
                spaceId,
                labelId: selectedLabel.id,
                reassignToLabelId: selectedReassignLabel?.id || null
            })).unwrap();

            message.success(selectedReassignLabel
                ? 'Label deleted and songs reassigned successfully'
                : 'Label deleted successfully');

            setDeleteModalVisible(false);
            setSelectedLabel(null);
            setSelectedReassignLabel(null);

            // Refresh labels
            dispatch(getLabelsWithCountsThunk(spaceId));
        } catch (error) {
            message.error('Failed to delete label: ' + (error.message || 'Unknown error'));
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOwner) {
        return (
            <>
                <h1>Label Management</h1>
                <Divider />
                <Alert
                    message="Access Denied"
                    description="Only space owners can manage labels."
                    type="warning"
                    showIcon
                />
            </>
        );
    }

    const columns = [
        {
            title: 'Label Name',
            dataIndex: 'label_name',
            key: 'label_name',
            width: '40%',
        },
        {
            title: 'Usage Count',
            dataIndex: 'usage_count',
            key: 'usage_count',
            width: '30%',
            align: 'center',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '30%',
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button
                        danger
                        type="primary"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteClick(record)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    const otherLabels = labelsWithCounts.filter(label => label.id !== selectedLabel?.id);

    return (
        <>
            <h1>Label Management</h1>
            <Divider />

            {labelsStatus === 'loading' ? (
                <Spin size="large" />
            ) : (
                <>
                    <Table
                        columns={columns}
                        dataSource={labelsWithCounts}
                        rowKey="id"
                        pagination={{ pageSize: 50 }}
                        className="label-management-table"
                    />

                    <Modal
                        title={`Delete Label: "${selectedLabel?.label_name}"`}
                        open={deleteModalVisible}
                        onCancel={() => {
                            setDeleteModalVisible(false);
                            setSelectedLabel(null);
                            setSelectedReassignLabel(null);
                        }}
                        onOk={handleDeleteConfirm}
                        confirmLoading={isDeleting}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        cancelText="Cancel"
                    >
                        {selectedLabel && selectedLabel.usage_count > 0 ? (
                            <>
                                <p>
                                    This label is used by <strong>{selectedLabel.usage_count}</strong> recording(s).
                                </p>
                                <p>Please select what to do with these recordings:</p>
                                <Select
                                    placeholder="Select an action..."
                                    style={{ width: '100%' }}
                                    value={selectedReassignLabel ? selectedReassignLabel.id : 'remove'}
                                    onChange={(value) => {
                                        if (value === 'remove') {
                                            setSelectedReassignLabel(null);
                                        } else {
                                            const label = otherLabels.find(l => l.id === value);
                                            setSelectedReassignLabel(label);
                                        }
                                    }}
                                    options={[
                                        {
                                            label: 'Remove label from all recordings (no reassignment)',
                                            value: 'remove'
                                        },
                                        ...(otherLabels.length > 0 ? [{
                                            label: 'Reassign to another label',
                                            options: otherLabels.map((label) => ({
                                                label: `${label.label_name} (${label.usage_count} recording(s))`,
                                                value: label.id
                                            }))
                                        }] : [])
                                    ]}
                                />
                            </>
                        ) : (
                            <p>
                                This label is not used by any recordings and will be deleted.
                            </p>
                        )}
                    </Modal>
                </>
            )}
        </>
    );
};
